import { useState } from 'react';

export default function WhatWeDo() {
  const [activePopup, setActivePopup] = useState<string | null>(null);

  const services = [
    {
      id: 'design',
      icon: 'ri-palette-line',
      title: 'Website Design & Launch',
      description: 'Custom WordPress websites designed to convert visitors into customers.',
      gradient: 'from-purple-500/10 to-purple-600/5',
      border: 'border-purple-500/20',
      iconBg: 'from-purple-500 to-purple-600',
      checkColor: 'text-purple-400',
      hoverGradient: 'hover:from-purple-500/15 hover:to-purple-600/10',
      details: {
        title: 'Website Design & Launch',
        description: 'Custom WordPress websites designed to convert visitors into customers.',
        features: [
          'Mobile-responsive design',
          'Fast loading optimisation',
          'SEO-friendly structure',
          'Brand-aligned styling',
          'Content management system',
          'SSL security setup',
          'Performance monitoring',
          'Cross-browser compatibility'
        ]
      }
    },
    {
      id: 'conversion',
      icon: 'ri-line-chart-line',
      title: 'Conversion Optimisation',
      description: 'Strategic layout and content optimisation to maximise conversions.',
      gradient: 'from-blue-500/10 to-blue-600/5',
      border: 'border-blue-500/20',
      iconBg: 'from-blue-500 to-blue-600',
      checkColor: 'text-blue-400',
      hoverGradient: 'hover:from-blue-500/15 hover:to-blue-600/10',
      details: {
        title: 'Conversion Optimisation',
        description: 'Strategic layout and content optimisation to maximise conversions.',
        features: [
          'A/B tested layouts',
          'Strategic CTA placement',
          'User experience optimisation',
          'Analytics integration',
          'Heatmap analysis',
          'Form optimisation',
          'Landing page design',
          'Conversion tracking setup'
        ]
      }
    },
    {
      id: 'booking',
      icon: 'ri-calendar-line',
      title: 'Booking & Payments',
      description: 'Seamless appointment booking and secure payment integration.',
      gradient: 'from-cyan-500/10 to-cyan-600/5',
      border: 'border-cyan-500/20',
      iconBg: 'from-cyan-500 to-cyan-600',
      checkColor: 'text-cyan-400',
      hoverGradient: 'hover:from-cyan-500/15 hover:to-cyan-600/10',
      details: {
        title: 'Booking & Payments',
        description: 'Seamless appointment booking and secure payment integration.',
        features: [
          'Online appointment system',
          'Payment gateway setup',
          'Automated confirmations',
          'Calendar synchronisation',
          'Recurring payment options',
          'Invoice generation',
          'Customer portal access',
          'Multi-timezone support'
        ]
      }
    },
    {
      id: 'management',
      icon: 'ri-tools-line',
      title: 'Ongoing Management',
      description: 'Monthly updates, security, and performance optimisation.',
      gradient: 'from-emerald-500/10 to-emerald-600/5',
      border: 'border-emerald-500/20',
      iconBg: 'from-emerald-500 to-emerald-600',
      checkColor: 'text-emerald-400',
      hoverGradient: 'hover:from-emerald-500/15 hover:to-emerald-600/10',
      details: {
        title: 'Ongoing Management',
        description: 'Monthly updates, security, and performance optimisation.',
        features: [
          'Regular content updates',
          'Security monitoring',
          'Performance optimisation',
          'Priority support',
          'Backup management',
          'Plugin updates',
          'Monthly reports',
          'Emergency response'
        ]
      }
    }
  ];

  const closePopup = () => setActivePopup(null);

  return (
    <>
      <section id="what-we-do" className="relative py-16 md:py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white to-purple-100 bg-clip-text text-transparent mb-4">
              What We Do
            </h2>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
              Transform your business with premium websites that drive real results
            </p>
          </div>

          {/* Service Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {services.map((service) => (
              <div
                key={service.id}
                className={`bg-gradient-to-br ${service.gradient} border ${service.border} rounded-2xl p-6 md:p-8 ${service.hoverGradient} transition-all duration-300 group`}
              >
                {/* Icon */}
                <div className={`w-14 h-14 bg-gradient-to-r ${service.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                  <i className={`${service.icon} text-white text-2xl`}></i>
                </div>

                {/* Content */}
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4 group-hover:text-purple-100 transition-colors duration-300">
                  {service.title}
                </h3>
                
                <p className="text-gray-300 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Learn More Button */}
                <button
                  onClick={() => setActivePopup(service.id)}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-purple-400 hover:text-purple-300 transition-colors duration-300 group cursor-pointer whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md px-2 py-1"
                  aria-label={`Learn more about ${service.title}`}
                >
                  Learn More
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Detail Popups */}
      {activePopup && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-lg rounded-3xl p-6 md:p-8 max-w-2xl w-full max-h-[85vh] overflow-y-auto border border-purple-500/20">
            {(() => {
              const service = services.find(s => s.id === activePopup);
              if (!service) return null;
              
              return (
                <>
                  {/* Header */}
                  <div className="flex justify-between items-center mb-6 md:mb-8">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 bg-gradient-to-r ${service.iconBg} rounded-lg flex items-center justify-center`}>
                        <i className={`${service.icon} text-white text-xl`}></i>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                        {service.details.title}
                      </h3>
                    </div>
                    <button
                      onClick={closePopup}
                      className="text-gray-400 hover:text-white text-2xl cursor-pointer transition-colors duration-300 w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>

                  {/* Description */}
                  <p className="text-gray-300 mb-6 md:mb-8 text-lg leading-relaxed">
                    {service.details.description}
                  </p>

                  {/* Features List */}
                  <div className="space-y-4 mb-6 md:mb-8">
                    <h4 className="text-xl font-semibold text-white mb-4">What's Included:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {service.details.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <i className={`ri-check-line ${service.checkColor} text-lg`}></i>
                          <span className="text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="text-center">
                    <button
                      onClick={() => {
                        closePopup();
                        const element = document.getElementById('free-audit');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold rounded-xl hover:from-purple-500 hover:to-blue-400 transition-all duration-300 transform hover:scale-105 cursor-pointer text-sm md:text-base whitespace-nowrap"
                    >
                      Get Started with Free Audit
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}
